import React, { useState, useEffect } from 'react';
import { Template, BusinessModel, TemplateTask } from '../types/template';
import { TemplateService } from '../services/templateService';
import './TemplateEditor.css';

interface TemplateEditorProps {
  isOpen: boolean;
  onClose: () => void;
  businessModel: BusinessModel;
  template: Template | null; // null for new template
  onSave: () => void;
}

const TemplateEditor: React.FC<TemplateEditorProps> = ({
  isOpen,
  onClose,
  businessModel,
  template,
  onSave
}) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [defaultTransportationVehicle, setDefaultTransportationVehicle] = useState('');
  const [defaultStatusOptions, setDefaultStatusOptions] = useState<string[]>([
    'Pending', 'In Progress', 'Completed', 'On Hold', 'Cancelled'
  ]);
  const [tasks, setTasks] = useState<TemplateTask[]>([]);
  const [isDefault, setIsDefault] = useState(false);
  const [isDefaultForCategory, setIsDefaultForCategory] = useState(false);
  const [defaultTransportationMethods, setDefaultTransportationMethods] = useState<string[]>([]);
  const [isActive, setIsActive] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      if (template) {
        // Editing existing template
        setName(template.name);
        setDescription(template.description);
        setCategoryId(template.categoryId);
        setDefaultTransportationVehicle(template.defaultTransportationVehicle);
        setDefaultStatusOptions(template.defaultStatusOptions);
        setTasks([...template.tasks]);
        setIsDefault(template.isDefault);
        setIsDefaultForCategory(template.isDefaultForCategory || false);
        setDefaultTransportationMethods(template.defaultTransportationMethods || []);
        setIsActive(template.isActive);
      } else {
        // Creating new template
        setName('');
        setDescription('');
        setCategoryId(businessModel.categories[0]?.id || '');
        setDefaultTransportationVehicle(businessModel.transportationMethods[0] || '');
        setDefaultStatusOptions(['Pending', 'In Progress', 'Completed', 'On Hold', 'Cancelled']);
        setTasks([{
          name: 'New Task',
          status: 'Pending',
          data: '',
          comments: '',
          customStatusOptions: []
        }]);
        setIsDefault(false);
        setIsDefaultForCategory(false);
        setDefaultTransportationMethods([]);
        setIsActive(true);
      }
    }
  }, [isOpen, template, businessModel]);

  const handleAddTask = () => {
    setTasks([...tasks, {
      name: 'New Task',
      status: 'Pending',
      data: '',
      comments: '',
      customStatusOptions: []
    }]);
  };

  const handleUpdateTask = (index: number, field: keyof TemplateTask, value: any) => {
    const updated = [...tasks];
    updated[index] = { ...updated[index], [field]: value };
    setTasks(updated);
  };

  const handleRemoveTask = (index: number) => {
    setTasks(tasks.filter((_, i) => i !== index));
  };

  const handleMoveTask = (index: number, direction: 'up' | 'down') => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= tasks.length) return;

    const updated = [...tasks];
    [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
    setTasks(updated);
  };

  const handleSave = async () => {
    if (!name.trim()) {
      setError('Template name is required');
      return;
    }

    if (tasks.length === 0) {
      setError('At least one task is required');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const templateData = {
        name,
        description,
        businessModelId: businessModel.id,
        categoryId,
        defaultTransportationVehicle,
        defaultStatusOptions,
        tasks,
        isDefault,
        isDefaultForCategory,
        defaultTransportationMethods,
        isActive,
        createdBy: 'MASTER'
      };

      if (template) {
        // Update existing template
        await TemplateService.updateTemplate(template.id, templateData);
      } else {
        // Create new template
        await TemplateService.createTemplate(templateData);
      }

      onSave();
      onClose();
    } catch (err) {
      console.error('Error saving template:', err);
      setError('Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="template-editor-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{template ? '✏️ Edit Template' : '➕ Create New Template'}</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          {error && (
            <div className="error-message">{error}</div>
          )}

          <div className="template-editor-grid">
            <div className="template-editor-left">
              <div className="editor-section">
                <label>Template Name *</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Enter template name"
                />
              </div>

              <div className="editor-section">
                <label>Description</label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Enter description"
                  rows={3}
                />
              </div>

              <div className="editor-section">
                <label>Category *</label>
                <select
                  value={categoryId}
                  onChange={(e) => setCategoryId(e.target.value)}
                >
                  {businessModel.categories.map(cat => (
                    <option key={cat.id} value={cat.id}>
                      {cat.icon} {cat.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="editor-section">
                <label>Default Transportation</label>
                <select
                  value={defaultTransportationVehicle}
                  onChange={(e) => setDefaultTransportationVehicle(e.target.value)}
                >
                  {businessModel.transportationMethods.map((method, idx) => (
                    <option key={idx} value={method}>{method}</option>
                  ))}
                </select>
              </div>

              <div className="editor-section">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={isDefault}
                    onChange={(e) => setIsDefault(e.target.checked)}
                  />
                  <span>Set as global default template</span>
                </label>
                <small style={{color: '#666', marginLeft: '24px', display: 'block'}}>
                  (Backward compatibility - one global default for entire app)
                </small>
              </div>

              <div className="editor-section">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={isDefaultForCategory}
                    onChange={(e) => setIsDefaultForCategory(e.target.checked)}
                  />
                  <span>Set as default for this category</span>
                </label>
                <small style={{color: '#666', marginLeft: '24px', display: 'block'}}>
                  (Recommended - smart defaults based on business context)
                </small>
              </div>

              {isDefaultForCategory && (
                <div className="editor-section">
                  <label>Apply to Transportation Methods</label>
                  <div className="transportation-methods-grid">
                    {businessModel.transportationMethods.map((method, idx) => (
                      <label key={idx} className="checkbox-label-small">
                        <input
                          type="checkbox"
                          checked={defaultTransportationMethods.includes(method)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setDefaultTransportationMethods([...defaultTransportationMethods, method]);
                            } else {
                              setDefaultTransportationMethods(defaultTransportationMethods.filter(m => m !== method));
                            }
                          }}
                        />
                        <span>{method}</span>
                      </label>
                    ))}
                  </div>
                  <small style={{color: '#666', marginTop: '8px', display: 'block'}}>
                    Select which transportation methods this template should be the default for
                  </small>
                </div>
              )}

              <div className="editor-section">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={isActive}
                    onChange={(e) => setIsActive(e.target.checked)}
                  />
                  <span>Active template</span>
                </label>
              </div>
            </div>

            <div className="template-editor-right">
              <div className="tasks-header">
                <label>Tasks ({tasks.length})</label>
                <button onClick={handleAddTask} className="add-task-btn-small">
                  + Add Task
                </button>
              </div>

              <div className="tasks-list">
                {tasks.map((task, index) => (
                  <div key={index} className="task-editor-item">
                    <div className="task-editor-header">
                      <span className="task-number">#{index + 1}</span>
                      <div className="task-controls">
                        <button
                          onClick={() => handleMoveTask(index, 'up')}
                          disabled={index === 0}
                          className="move-btn"
                          title="Move up"
                        >
                          ↑
                        </button>
                        <button
                          onClick={() => handleMoveTask(index, 'down')}
                          disabled={index === tasks.length - 1}
                          className="move-btn"
                          title="Move down"
                        >
                          ↓
                        </button>
                        <button
                          onClick={() => handleRemoveTask(index)}
                          className="remove-task-btn"
                          title="Remove task"
                        >
                          ×
                        </button>
                      </div>
                    </div>

                    <input
                      type="text"
                      value={task.name}
                      onChange={(e) => handleUpdateTask(index, 'name', e.target.value)}
                      placeholder="Task name"
                      className="task-name-input"
                    />

                    <div className="task-row">
                      <div className="task-field">
                        <label>Default Status</label>
                        <select
                          value={task.status}
                          onChange={(e) => handleUpdateTask(index, 'status', e.target.value)}
                        >
                          {defaultStatusOptions.map((status, idx) => (
                            <option key={idx} value={status}>{status}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <button onClick={handleAddTask} className="add-task-btn-large">
                + Add Another Task
              </button>
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <button onClick={onClose} className="cancel-btn" disabled={saving}>
            Cancel
          </button>
          <button onClick={handleSave} className="save-btn" disabled={saving}>
            {saving ? 'Saving...' : (template ? 'Update Template' : 'Create Template')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TemplateEditor;

